import Direction from '../../../../shared/types/enums/direction';
import IAction from '../../../../shared/types/interfaces/action';
import ListContext from '../../../../shared/components/utils/context/listContext';
import Props from '../../../../shared/types/props/list';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';
import { ReactElement, useState, useRef, useEffect } from 'react';
import { focusSelector } from '../../../../shared/store/slices/focus.slice';
import { useAppSelector } from '../../../../shared/store/hooks';

function List({
  children,
  direction = Direction.HORIZONTAL,
  fixed,
  focus,
  initialIndex,
  numberOfPressures = 3,
  optionalSelect,
  setListIndex,
  outOfBounds,
  onChange,
}: Props) {
  const { focus: currentFocus } = useAppSelector(focusSelector);
  const [index, setIndex] = useState<number>(initialIndex || 0);
  const childrenRef = useRef(children);
  const focusedRef = useRef<boolean>(false);
  const indexRef = useRef<number>(index);
  const lastRef = useRef<number>(-1);
  childrenRef.current = children;
  focusedRef.current =
    focus === currentFocus ||
    ((currentFocus as (number | string)[])?.length
      ? (currentFocus as (number | string)[]).includes(focus + '') ||
        (currentFocus as (number | string)[]).includes(focus as number)
      : false);
  indexRef.current = index;

  useEffect(() => {
    const childrenLength = childrenRef?.current?.length || 0;
    if (
      !focusedRef.current &&
      (indexRef.current === 0 || indexRef.current === childrenLength - 1)
    ) {
      lastRef.current = indexRef.current;
    }
  }, [currentFocus]);

  useEffect(() => {
    lastRef.current = -1;
  }, []);

  useKeyboard({
    focus,
    keyDownHandler: (key, pressed, pressures) => {
      if (pressures && pressures % numberOfPressures !== 0 && pressures != 1)
        return;
      if (!focusedRef.current) return;
      setIndex((index) => {
        let newIndex = index;
        const childrenLength = childrenRef?.current?.length || 0;
        let increment = 0;
        if (
          ((key === 'RIGHT' && direction === Direction.HORIZONTAL) ||
            (key == 'DOWN' && direction === Direction.VERTICAL)) &&
          childrenLength > index + 1
        ) {
          newIndex = index + 1;
          increment = 1;
        } else if (
          ((key === 'LEFT' && direction === Direction.HORIZONTAL) ||
            (key == 'UP' && direction === Direction.VERTICAL)) &&
          index > 0
        ) {
          newIndex = index - 1;
          increment = -1;
        }
        if (onChange && increment) {
          onChange(newIndex, increment);
        } else if (outOfBounds && focus && increment === 0) {
          setTimeout(() => {
            outOfBounds(focus, key);
          });
        }
        return newIndex;
      });
      if (
        (key === 'SELECT' || key === optionalSelect) &&
        indexRef.current !== -1
      ) {
        const child =
          childrenRef.current &&
          (childrenRef.current[indexRef.current] as ReactElement<IAction>);
        if (child?.props?.action) {
          child?.props.action(indexRef.current);
        }
      }
    },
    keyUpHandler: (key) => {
      if (!focusedRef.current) return;
      if (key === 'SELECT') {
        const child =
          childrenRef.current &&
          (childrenRef.current[indexRef.current] as ReactElement<IAction>);
        if (child?.props?.releaseAction) {
          child?.props.releaseAction(indexRef.current);
        }
      }
    },
  });

  const value = {
    listFixed: fixed,
    listFocused: focusedRef.current,
    listIndex: index,
    onChange: onChange,
    setListIndex: (newIndex: number, increment?: number) => {
      if (increment) {
        setIndex((oldIndex) => {
          const newIndex = oldIndex + increment;
          if (newIndex >= 0 && newIndex < (children?.length || 0)) {
            onChange && onChange(oldIndex + increment, increment);
            return oldIndex + increment;
          }
          return oldIndex;
        });
      } else {
        if (newIndex >= 0 && newIndex < (children?.length || 0)) {
          setIndex(newIndex);
          onChange && onChange(newIndex, newIndex - index);
        }
      }
    },
  };

  useEffect(() => {
    if (setListIndex) {
      setListIndex.current = value.setListIndex;
    }
  }, [children]);

  return <ListContext.Provider value={value}>{children}</ListContext.Provider>;
}

export default List;
